import React, { useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import styled from 'styled-components';

const AutoCropSizer = ({
  mediaNaturalWidth,
  mediaNaturalHeight,
  children,
  onSizeChange,
}: {
  mediaNaturalWidth?: number;
  mediaNaturalHeight?: number;
  onSizeChange: (height: number, width: number) => void;
  children: React.ReactNode;
}) => {
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);

  const calculateNewMaxDimensions = (clientWidth: number, clientHeight: number) => {
    if (mediaNaturalHeight && mediaNaturalWidth) {
      // if the image is smaller than the client -- easy peasy
      if (mediaNaturalHeight < clientHeight && mediaNaturalWidth < clientWidth) {
        setMaxWidth(mediaNaturalWidth);
        setMaxHeight(mediaNaturalHeight);
        onSizeChange(mediaNaturalHeight, mediaNaturalWidth);
        return;
      }

      // else we need to scale :sweaty
      const ratio = mediaNaturalHeight / mediaNaturalWidth;
      if (mediaNaturalHeight > mediaNaturalWidth) {
        const newWidth = clientHeight / ratio;
        const newHeight = clientHeight;
        if (newWidth > clientWidth) {
          const finalWidth = clientWidth;
          const finalHeight = clientWidth * ratio;
          setMaxWidth(finalWidth);
          setMaxHeight(finalHeight);
          onSizeChange(finalHeight, finalWidth);
        } else {
          setMaxWidth(newWidth);
          setMaxHeight(newHeight);
          onSizeChange(newHeight, newWidth);
        }
      } else {
        const newWidth = clientWidth;
        const newHeight = clientWidth * ratio;

        if (newHeight > clientHeight) {
          const finalWidth = clientHeight / ratio;
          const finalHeight = clientHeight;
          setMaxWidth(finalWidth);
          setMaxHeight(finalHeight);
          onSizeChange(finalHeight, finalWidth);
        } else {
          setMaxWidth(newWidth);
          setMaxHeight(newHeight);
          onSizeChange(newHeight, newWidth);
        }
      }
    }
  };
  useEffect(() => {
    calculateNewMaxDimensions(clientWidth, clientHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaNaturalHeight, mediaNaturalWidth, clientWidth, clientHeight]);

  const observer = useRef(
    new ResizeObserver(entries => {
      // We wrap it in requestAnimationFrame to avoid this error - ResizeObserver loop limit exceeded
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        // Only care about the first element, we expect one element ot be watched
        // Gets width and height of the parent div container
        const { width, height } = entries[0].contentRect;
        setClientWidth(width);
        setClientHeight(height);
        // must use the media natural height and width to calculate the max dimensions it can get
        calculateNewMaxDimensions(width, height);
      });
    }),
  );

  useEffect(() => {
    const observerRef = observer.current;
    const mediaRef = mediaContainerRef.current;
    if (mediaRef) {
      observer.current.observe(mediaRef);
    }

    return () => {
      if (mediaRef && observerRef) {
        observerRef.unobserve(mediaRef);
      }
    };
  }, [mediaContainerRef, observer]);

  return (
    <MediaContainer ref={mediaContainerRef}>
      <MediaCropContainer $height={maxHeight} $width={maxWidth}>
        {children}
      </MediaCropContainer>
    </MediaContainer>
  );
};

export default AutoCropSizer;

const MediaContainer = styled.div`
  height: 100%;
  width: 100%;
`;
const MediaCropContainer = styled.div<{ $height: number; $width: number }>`
  height: ${({ $height }) => $height}px;
  width: ${({ $width }) => $width}px;
  margin: auto;
`;
